/**
 * Freetrial (public, no-auth demo calendar) query/mutation hooks.
 * useEventData is shared with the authenticated calendar — see
 * src/queries/calendar.js — import it from there instead of duplicating it.
 */
import { useMutation } from "@tanstack/react-query";
import localforage from "localforage";
import { request, ReqMethods } from "../lib/apiClient";
import env from "../config/env";

const { POST, PUT } = ReqMethods;

// POST env.API_URL + "/auth" — NOT /v1, unlike every other endpoint in the
// app. Preserved exactly: apiClient's shared baseURL always adds /v1, so
// this passes an absolute URL (`${env.API_URL}/auth`) straight through —
// axios's buildFullPath ignores baseURL whenever the request url is already
// absolute. Do not "fix" this to go through /v1, that would hit a different
// backend route and could break the demo signup flow.
export function useFreetrialSignup() {
  return useMutation({
    /** @param {Record<string, any>} payload */
    mutationFn: (payload) => request(POST, `${env.API_URL}/auth`, { payload }),
  });
}

// Freetrial/components/day's "Write me a Post" action: PUT /user/updateuser
// (bumps + returns the caller's usage count) -> POST /chat/${API_ENV}
// (generates the post) — but ONLY when the local usage cap (from the
// *previous* PUT's cached count) hasn't already been hit. Modeled as one
// mutation so the cap check + PUT + POST stay a single sequence, matching
// the original handleRequest's control flow exactly:
//   1. read the previously-cached calendar_res from localforage
//   2. if it's already >= 10, stop here (limitReached: true) — the PUT for
//      *this* call never happens, and the caller opens LimitModal
//   3. otherwise, if we know who the user is, PUT to bump usage, cache the
//      fresh count for the *next* call's gate check, then POST for content
//   4. if we don't know who the user is yet (no user_data), skip the PUT
//      and POST directly (anonymous path, same as the original)
export function useFreetrialDayPost() {
  return useMutation({
    /** @param {{title: string, date: string}} vars */
    mutationFn: async ({ title, date }) => {
      const user_data = await localforage.getItem("user_data");
      const calendar_res = await localforage.getItem("calendar_res");

      if (calendar_res !== null && calendar_res >= 10) {
        return { limitReached: true, content: null };
      }

      if (user_data) {
        const putRes = await request(PUT, "/user/updateuser", {
          query: `email=${encodeURIComponent(user_data?.email)}`,
          payload: {},
        });
        await localforage.setItem("calendar_res", putRes?.data?.calendar_res);
        const postRes = await request(POST, `/chat/${env.API_ENV}`, {
          payload: { date, event_title: title, user_name: user_data?.username },
        });
        return { limitReached: false, content: postRes?.data };
      }

      const postRes = await request(POST, `/chat/${env.API_ENV}`, {
        payload: { date, event_title: title },
      });
      return { limitReached: false, content: postRes?.data };
    },
  });
}
